import React, { useEffect, useState } from 'react';
import { prettifyUtcDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import VerificationStatusText from '../ui/status-icon/VerificationStatusText';
import { VerificationTable } from '../../pages/verification/Verification.styles';
import { ListButton } from '../../styles/button.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import { useVerificationStore } from '../../context/VerificationContext';
import {
    canReportBeSentToDnv,
    canShowComponentForRole,
    disableSimpleSendButton,
    getReportTypeString,
    getVerificationTooltip,
    isReportMetadataChecked,
    numInternalIssues,
    showMultiSendCheckbox,
    sortList,
} from '../../utils/verification/verificationUtils';
import { DNVReportStatus } from '../../enums/dnvReportStatusEnum';
import { SortColumnEnum } from '../../enums/sortVerificationColumnEnum';
import { useAuthStore } from '../../context/AuthContext';

type Props = {
    isRightPanelExpanded: boolean;
    sendReportToDNV: (ReportMetadata) => void;
    sendSelectedReportsToDNV: () => void;
};

const ReportMetadataListComponent: React.FC<Props> = (props) => {
    const [sortColumn, setSortColumn] = useState<SortColumnEnum>(SortColumnEnum.PeriodStart);
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const { role } = useAuthStore();
    const {
        reportComment,
        setReportComment,
        verificationFilter,
        selectedReportMetadata,
        setSelectedReportMetadata,
        filteredReportMetadataList,
        setFilteredReportMetadataList,
        populatedReportMetadataList,
        setNumFilteredReports,
        selectedReportMetadataList,
        setSelectedReportMetadataList,
    } = useVerificationStore();

    useEffect(() => {
        if (populatedReportMetadataList && populatedReportMetadataList.length > 0 && verificationFilter) {
            let _filteredReportMetadataList = populatedReportMetadataList;

            if (verificationFilter) {
                const selVessels = verificationFilter.selectedVessels;
                if (selVessels) {
                    if (selVessels.length === 0) {
                        // explicit none -> no reports
                        _filteredReportMetadataList = [];
                    } else {
                        _filteredReportMetadataList = _filteredReportMetadataList.filter((r) =>
                            selVessels.some((v) => v.value === r.imoNumber.toString())
                        );
                    }
                }
            }
            if (verificationFilter && verificationFilter.selectedManagers) {
                _filteredReportMetadataList = _filteredReportMetadataList.filter((r) =>
                    verificationFilter.selectedManagers?.some((v) => v.value === r.manager.toString())
                );
            }
            if (verificationFilter && verificationFilter.selectedStatuses) {
                _filteredReportMetadataList = _filteredReportMetadataList.filter((r) =>
                    verificationFilter.selectedStatuses?.some((v) => v.value === r.status.toString())
                );
            }

            if (verificationFilter && verificationFilter.selectedTypes) {
                _filteredReportMetadataList = _filteredReportMetadataList.filter((r) =>
                    verificationFilter.selectedTypes?.some((v) => v.value === r.reportType.toString())
                );
            }

            if (verificationFilter && verificationFilter.selectedYears) {
                _filteredReportMetadataList = _filteredReportMetadataList.filter((r) =>
                    verificationFilter.selectedYears?.some(
                        (v) =>
                            Number(v.value) === new Date(r.voyageDateTimeUtcFrom).getFullYear() ||
                            Number(v.value) === new Date(r.voyageDateTimeUtcTo).getFullYear()
                    )
                );
            }

            if (verificationFilter) {
                const sel = verificationFilter.selectedChecks;
                const all = verificationFilter.availableChecks;
                if (sel && sel.length === 0) {
                    //explicit none -> no reports
                    _filteredReportMetadataList = [];
                } else if (sel && all && sel.length !== all.length) {
                    // subset filter
                    _filteredReportMetadataList = _filteredReportMetadataList.filter((r) => {
                        if (!r.dnvIssues || r.dnvIssues.length === 0) return false;
                        return r.dnvIssues.some((iss) => sel.some((c) => c.value === (iss.checkNumber != null ? iss.checkNumber.toString() : '')));
                    });
                }
                // sel == null OR sel length equals all length => no filtering
            }

            _filteredReportMetadataList = sortList(_filteredReportMetadataList, sortColumn, sortDesc);

            if (_filteredReportMetadataList && _filteredReportMetadataList.length > 0) {
                if (!selectedReportMetadata || !_filteredReportMetadataList.find((f) => f.reportId === selectedReportMetadata?.reportId)) {
                    setSelectedReportMetadata(_filteredReportMetadataList[0]);
                }
            } else if (!_filteredReportMetadataList || _filteredReportMetadataList.length == 0) {
                setSelectedReportMetadata(null);
            }

            setFilteredReportMetadataList([..._filteredReportMetadataList]);
            setNumFilteredReports(_filteredReportMetadataList.length);
        }
    }, [populatedReportMetadataList, verificationFilter]);

    useEffect(() => {
        const _washedSelectedReportMetadataList = [];
        if (filteredReportMetadataList && selectedReportMetadataList) {
            selectedReportMetadataList.forEach((r) => {
                if (filteredReportMetadataList.find((f) => f.reportId === r.reportId)) {
                    _washedSelectedReportMetadataList.push(r);
                }
            });
            setSelectedReportMetadataList([..._washedSelectedReportMetadataList]);
        }
    }, [filteredReportMetadataList]);

    const onChangeSort = (sortCol: SortColumnEnum) => {
        let _isSortDesc = sortDesc;

        if (sortCol === sortColumn && _isSortDesc) {
            _isSortDesc = false;
        } else if (sortCol === sortColumn && !_isSortDesc) {
            _isSortDesc = true;
        } else {
            _isSortDesc = false;
        }

        let _filteredReportMetadataList = filteredReportMetadataList;
        _filteredReportMetadataList = sortList(_filteredReportMetadataList, sortCol, _isSortDesc);

        setFilteredReportMetadataList([..._filteredReportMetadataList]);
        setSortColumn(sortCol);
        setSortDesc(_isSortDesc);
    };

    const onSelectedReportMetadata = (reportMetadata: ReportMetadata) => {
        const _reportComment = reportComment;
        _reportComment.reportId = reportMetadata.reportId;
        _reportComment.comment = reportMetadata.comment;
        setReportComment({ ..._reportComment });

        setSelectedReportMetadata({ ...reportMetadata });
    };

    const getVerificationCounts = (v: ReportMetadata) => {
        if (v.status === DNVReportStatus.VoyageOngoing || v.status === DNVReportStatus.ReadyForVerification) {
            return <></>;
        } else {
            return (
                <>
                    <span>{numInternalIssues(v)}</span>|
                    <span>
                        {v.verificationList?.filter((v) => v.severity === 'Issue')
                            ? v.verificationList?.filter((v) => v.severity === 'Issue').length
                            : 0}
                    </span>
                    |
                    <span>
                        {v.verificationList?.filter((v) => v.severity === 'Warning')
                            ? v.verificationList?.filter((v) => v.severity === 'Warning').length
                            : 0}
                    </span>
                </>
            );
        }
    };

    const onIsAllCheckedChange = () => {
        if (isAllSelected) {
            setSelectedReportMetadataList([]);
        } else {
            const _filteredReportMetadataList = filteredReportMetadataList.filter((r) => {
                return canReportBeSentToDnv(r);
            });

            setSelectedReportMetadataList([..._filteredReportMetadataList]);
        }
        setIsAllSelected(!isAllSelected);
    };

    const onReportMetadataClick = (reportMetadata: ReportMetadata) => {
        let _selectedReportMetadataList = selectedReportMetadataList ? selectedReportMetadataList : [];
        if (_selectedReportMetadataList?.some((r) => r.reportId === reportMetadata.reportId)) {
            _selectedReportMetadataList = _selectedReportMetadataList.filter((r) => r.reportId !== reportMetadata.reportId);
        } else {
            _selectedReportMetadataList.push(reportMetadata);
        }

        let _allChecked = true;

        const _filteredReportMetadataList = filteredReportMetadataList.filter((r) => {
            return canReportBeSentToDnv(r);
        });

        _filteredReportMetadataList.forEach((r) => {
            if (!_selectedReportMetadataList.find((f) => f.reportId === r.reportId)) {
                _allChecked = false;
            }
        });

        if (_allChecked) {
            setIsAllSelected(true);
        } else {
            setIsAllSelected(false);
        }

        setSelectedReportMetadataList([..._selectedReportMetadataList]);
    };

    const checkShowSendAllButton = () => {
        if ((selectedReportMetadataList && selectedReportMetadataList.length > 0) || isAllSelected) {
            return true;
        } else {
            return false;
        }
    };

    const checkShowSendSimpleButton = () => {
        if ((selectedReportMetadataList && selectedReportMetadataList.length > 0) || isAllSelected) {
            return false;
        } else {
            return true;
        }
    };

    return (
        <VerificationTable isExpanded={props.isRightPanelExpanded}>
            <ul className="verification-table header">
                <li key={'header_item_1'}>
                    <div className="col col-vessel-name" onClick={() => onChangeSort(SortColumnEnum.VesselName)}>
                        Vessel
                        {sortColumn === SortColumnEnum.VesselName && (
                            <span>
                                {' '}
                                <FontAwesomeIcon
                                    className="sort-icon"
                                    title="Click to change sorting"
                                    icon={sortDesc && sortColumn === SortColumnEnum.VesselName ? 'arrow-up' : 'arrow-down'}
                                />
                            </span>
                        )}
                    </div>
                    <div className="col" onClick={() => onChangeSort(SortColumnEnum.ReportType)}>
                        Type
                        {sortColumn === SortColumnEnum.ReportType && (
                            <span>
                                {' '}
                                <FontAwesomeIcon
                                    className="sort-icon"
                                    title="Click to change sorting"
                                    icon={sortDesc && sortColumn === SortColumnEnum.ReportType ? 'arrow-up' : 'arrow-down'}
                                />
                            </span>
                        )}
                    </div>
                    <div className="col col-date" title="Voyage started" onClick={() => onChangeSort(SortColumnEnum.PeriodStart)}>
                        From
                        {sortColumn === SortColumnEnum.PeriodStart && (
                            <span>
                                {' '}
                                <FontAwesomeIcon
                                    className="sort-icon"
                                    title="Click to change sorting"
                                    icon={sortDesc && sortColumn === SortColumnEnum.PeriodStart ? 'arrow-up' : 'arrow-down'}
                                />
                            </span>
                        )}
                    </div>
                    <div className="col col-date" title="Voyage ended" onClick={() => onChangeSort(SortColumnEnum.PeriodEnd)}>
                        To
                        {sortColumn === SortColumnEnum.PeriodEnd && (
                            <span>
                                {' '}
                                <FontAwesomeIcon
                                    className="sort-icon"
                                    title="Click to change sorting"
                                    icon={sortDesc && sortColumn === SortColumnEnum.PeriodEnd ? 'arrow-up' : 'arrow-down'}
                                />
                            </span>
                        )}
                    </div>
                    {!props.isRightPanelExpanded && (
                        <>
                            <div className="col col-date" title="Report was sent to DNV (UTC)" onClick={() => onChangeSort(SortColumnEnum.SentToDNV)}>
                                Sent to DNV (UTC)
                                {sortColumn === SortColumnEnum.SentToDNV && (
                                    <span>
                                        {' '}
                                        <FontAwesomeIcon
                                            className="sort-icon"
                                            title="Click to change sorting"
                                            icon={sortDesc && sortColumn === SortColumnEnum.SentToDNV ? 'arrow-up' : 'arrow-down'}
                                        />
                                    </span>
                                )}
                            </div>
                            <div
                                className="col col-date"
                                title="Verification received from DNV (UTC)"
                                onClick={() => onChangeSort(SortColumnEnum.VerificationDate)}
                            >
                                Verification (UTC)
                                {sortColumn === SortColumnEnum.VerificationDate && (
                                    <span>
                                        {' '}
                                        <FontAwesomeIcon
                                            className="sort-icon"
                                            title="Click to change sorting"
                                            icon={sortDesc && sortColumn === SortColumnEnum.VerificationDate ? 'arrow-up' : 'arrow-down'}
                                        />
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    {!props.isRightPanelExpanded && (
                        <div
                            className="col col-center col-issues"
                            title="Number of issues and warnings recived from DNV (UTC)"
                            onClick={() => onChangeSort(SortColumnEnum.NumberOfIssues)}
                        >
                            Issues
                            {sortColumn === SortColumnEnum.NumberOfIssues && (
                                <span>
                                    {' '}
                                    <FontAwesomeIcon
                                        className="sort-icon"
                                        title="Click to change sorting"
                                        icon={sortDesc && sortColumn === SortColumnEnum.NumberOfIssues ? 'arrow-up' : 'arrow-down'}
                                    />
                                </span>
                            )}
                        </div>
                    )}{' '}
                    {props.isRightPanelExpanded && (
                        <div
                            className="col narrow"
                            title="Number of issues and warnings recived from DNV (UTC)"
                            onClick={() => onChangeSort(SortColumnEnum.NumberOfIssues)}
                        >
                            Issues{' '}
                            {sortColumn === SortColumnEnum.NumberOfIssues && (
                                <span>
                                    {' '}
                                    <FontAwesomeIcon
                                        className="sort-icon"
                                        title="Click to change sorting"
                                        icon={sortDesc && sortColumn === SortColumnEnum.NumberOfIssues ? 'arrow-up' : 'arrow-down'}
                                    />
                                </span>
                            )}
                        </div>
                    )}
                    <div className="col col-status" onClick={() => onChangeSort(SortColumnEnum.Status)}>
                        Status
                        {sortColumn === SortColumnEnum.Status && (
                            <span>
                                {' '}
                                <FontAwesomeIcon
                                    className="sort-icon"
                                    title="Click to change sorting"
                                    icon={sortDesc && sortColumn === SortColumnEnum.Status ? 'arrow-up' : 'arrow-down'}
                                />
                            </span>
                        )}
                    </div>
                    <div className="col col-comment col-center"></div>
                    <div className="col col-checkbox">
                        {canShowComponentForRole(role) && (
                            <>
                                {selectedReportMetadataList && selectedReportMetadataList.length > 0 && (
                                    <span>({selectedReportMetadataList.length})</span>
                                )}
                                <div className="checkbox-wrapper">
                                    <input type="checkbox" checked={isAllSelected} onChange={onIsAllCheckedChange}></input>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="col col-center col-send">
                        {checkShowSendAllButton() && canShowComponentForRole(role) && (
                            <ListButton type="button" onClick={() => props.sendSelectedReportsToDNV()}>
                                Send
                            </ListButton>
                        )}
                    </div>
                </li>
            </ul>
            {filteredReportMetadataList && filteredReportMetadataList.length > 0 && (
                <ul className={props.isRightPanelExpanded ? 'verification-table list expanded' : 'verification-table list'}>
                    {filteredReportMetadataList.map((v) => {
                        return (
                            <li
                                key={'report_' + v.reportId}
                                className={v.reportId === selectedReportMetadata?.reportId ? 'selected' : ''}
                                onClick={() => onSelectedReportMetadata(v)}
                            >
                                <div className="col col-vessel-name">{v.vesselName}</div>
                                <div className="col">{getReportTypeString(v.reportType)}</div>
                                <div className="col col-date">
                                    {prettifyUtcDate(v.voyageDateTimeUtcFrom)} {v.portFromUnCode}
                                </div>
                                <div className="col col-date">
                                    {prettifyUtcDate(v.voyageDateTimeUtcTo)} {v.portToUnCode}
                                </div>
                                {!props.isRightPanelExpanded && (
                                    <>
                                        <div className={'col col-date'}>{prettifyUtcDate(v.sentToDNVAtTimestamp)}</div>
                                        <div className={'col col-date'}>
                                            {v.verificationList && v.verificationList.length > 0 ? prettifyUtcDate(v.maxVerificationDate) : ''}
                                        </div>
                                    </>
                                )}

                                {!props.isRightPanelExpanded && (
                                    <div title={getVerificationTooltip(v)} className={'col col-center col-issues'}>
                                        {getVerificationCounts(v)}
                                    </div>
                                )}
                                {props.isRightPanelExpanded && (
                                    <div title={getVerificationTooltip(v)} className={'col col-center narrow'}>
                                        {getVerificationCounts(v)}
                                    </div>
                                )}

                                {!props.isRightPanelExpanded && <div className={'col col-status'}>{VerificationStatusText(v.status)}</div>}
                                {props.isRightPanelExpanded && <div className={'col col-status'}>{VerificationStatusText(v.status)}</div>}
                                <div className={'col col-comment col-center'}>
                                    {v.comment && <FontAwesomeIcon className="icon" title={v.comment} icon={'comment'} />}
                                </div>
                                <div className="col col-checkbox">
                                    <div
                                        className="checkbox-wrapper"
                                        onClick={(e) => {
                                            onReportMetadataClick(v);
                                            e.stopPropagation();
                                        }}
                                    >
                                        {showMultiSendCheckbox(v) && canShowComponentForRole(role) && (
                                            <input
                                                type="checkbox"
                                                checked={isReportMetadataChecked(selectedReportMetadataList, v)}
                                                readOnly={true}
                                            ></input>
                                        )}
                                    </div>
                                </div>

                                <div className={'col col-send col-center'}>
                                    {checkShowSendSimpleButton() && canShowComponentForRole(role) && (
                                        <ListButton type="button" disabled={disableSimpleSendButton(v)} onClick={() => props.sendReportToDNV(v)}>
                                            Send
                                        </ListButton>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </VerificationTable>
    );
};
export default ReportMetadataListComponent;
